import React, { useEffect, useState } from "react";
import { ACTIVITY_COLORS, ACTIVITY_LABELS } from "@/lib/api";
import { Send, Eye, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLongPress } from "@/lib/useLongPress";
import { isoWeekOf, rowMondayDate, colorOf } from "@/lib/timesheetUtils";
import Flag from "@/components/Flag";

/**
 * Iter-115: detect mobile (< 768px) so the calendar can route taps to a bottom sheet
 * instead of the legacy click-to-add-2h gesture (which made it impossible to delete
 * a chip cleanly because chips are 10px tall).
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

/**
 * Iter-193c — country-flag rendering now uses the cross-platform `<Flag />`
 * component (image from flagcdn.com) instead of regional-indicator emoji that
 * don't render on Chrome Windows nor on older Android. The helper is kept as a
 * boolean check ("is this a valid 2-letter ISO code?") to decide whether to
 * render a flag image.
 */
function isValidCountryCode(code) {
  if (!code || typeof code !== "string") return false;
  const cc = code.trim().toUpperCase();
  return cc.length === 2 && /^[A-Z]{2}$/.test(cc);
}


/**
 * Grille du calendrier mensuel + cellule Send hebdomadaire à gauche de chaque ligne
 * + chips d'imputation par jour. Toute la logique de rendu/clic vit ici ; la state
 * applicative reste dans le parent (`Timesheet.jsx`).
 */
export default function CalendarGrid({
  year,
  month,
  cells,
  today,
  missions,
  entriesByDay,
  holidays,
  weeklySubs,
  selectedActivity,
  selectedMission,
  isImpersonating,
  locked,
  user,
  leaveBalance,
  congeOverflowMap,
  onDayClick,
  onDeleteEntry,
  onOpenWeeklyCra,
  onOpenDaySheet,
  weeklyCraMissions = [],
}) {
  const isMobile = useIsMobile();
  // Group cells into rows of 7 (one row = one week-row in the month grid).
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-[44px_repeat(7,1fr)] border-b border-zinc-200">
        <div className="px-2 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono border-r border-zinc-100 text-center">CRA</div>
        {["Mon", "Mar", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div
            key={d}
            className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono border-r border-zinc-100 last:border-r-0"
          >
            {d}
          </div>
        ))}
      </div>
      <div>
        {rows.map((row, rowIdx) => (
          <CalendarRow
            key={rowIdx}
            row={row}
            year={year}
            month={month}
            today={today}
            missions={missions}
            entriesByDay={entriesByDay}
            holidays={holidays}
            weeklySubs={weeklySubs}
            selectedActivity={selectedActivity}
            selectedMission={selectedMission}
            isImpersonating={isImpersonating}
            locked={locked}
            user={user}
            leaveBalance={leaveBalance}
            congeOverflowMap={congeOverflowMap}
            onDayClick={onDayClick}
            onDeleteEntry={onDeleteEntry}
            onOpenWeeklyCra={onOpenWeeklyCra}
            onOpenDaySheet={onOpenDaySheet}
            isMobile={isMobile}
            weeklyCraMissions={weeklyCraMissions}
          />
        ))}
      </div>
    </div>
  );
}

function CalendarRow({
  row, year, month, today, missions, entriesByDay, holidays, weeklySubs,
  selectedActivity, selectedMission, isImpersonating, locked, user,
  leaveBalance, congeOverflowMap, onDayClick, onDeleteEntry, onOpenWeeklyCra,
  onOpenDaySheet, isMobile, weeklyCraMissions = [],
}) {
  const monday = rowMondayDate(year, month, row);
  const isoMeta = monday ? isoWeekOf(monday) : null;
  const hasWeekday = row.slice(0, 5).some((d) => d != null);
  const targetMissionId = selectedActivity === "MISSION" && selectedMission
    ? selectedMission
    : (missions[0]?.mission_id || "");
  // Iter-203 — Only render the Weekly CRA cell when the chosen mission is opted-in.
  const targetMissionIsWeeklyCraEnabled = weeklyCraMissions.some((m) => m.mission_id === targetMissionId);
  // Iter-131: in impersonation mode the Weekly CRA button must remain visible so the
  // CEO/ADMIN can review (read-only) or submit on behalf (after clicking Edit) — the
  // WeeklyCraDialog itself handles the read-only / edit-on-behalf state.
  const showWeekBtn = hasWeekday && missions.length > 0 && isoMeta && targetMissionIsWeeklyCraEnabled;
  const targetMission = missions.find((m) => m.mission_id === targetMissionId);
  const sentForThisWeek = isoMeta
    ? weeklySubs.filter((s) => s.year === isoMeta.year && s.iso_week === isoMeta.week)
    : [];
  const sentForTarget = sentForThisWeek.find((s) => s.mission_id === targetMissionId);

  // Iter-82: future-week guard.
  const isFutureRow = (() => {
    if (!monday) return false;
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return monday > todayDate;
  })();
  const isCurrentRow = (() => {
    if (!monday) return false;
    const t = isoWeekOf(today);
    return isoMeta && t.year === isoMeta.year && t.week === isoMeta.week;
  })();

  const hoursThisWeek = (() => {
    if (!targetMissionId || !monday) return 0;
    let total = 0;
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday); d.setDate(d.getDate() + i);
      if (d.getFullYear() === year && d.getMonth() + 1 === month) {
        const day = d.getDate();
        (entriesByDay[day] || []).forEach((e) => {
          if (e.activity_type === "MISSION" && e.mission_id === targetMissionId) {
            total += Number(e.hours) || 0;
          }
        });
      }
    }
    return total;
  })();

  // Iter-109: total imputed hours on the week (Mon..Fri, all activities).
  const weekTotalHours = (() => {
    if (!monday) return 0;
    let total = 0;
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday); d.setDate(d.getDate() + i);
      if (d.getFullYear() === year && d.getMonth() + 1 === month) {
        const day = d.getDate();
        (entriesByDay[day] || []).forEach((e) => {
          total += Number(e.hours) || 0;
        });
      }
    }
    return total;
  })();
  const weekTotalDays = weekTotalHours / 8;

  const handleOpenWeeklyCra = () => {
    // Past weeks with zero hours → friendly toast (user choice "f").
    if (!sentForTarget && !isFutureRow && !isCurrentRow && hoursThisWeek === 0) {
      toast.message(
        `Aucune heure loggée cette semaine sur ${targetMission?.title || "cette mission"}.`,
        { description: "Imputez vos heures dans le timesheet avant d'envoyer le CRA hebdomadaire." },
      );
      return;
    }
    onOpenWeeklyCra({
      missionId: targetMissionId,
      isoYear: isoMeta.year,
      isoWeek: isoMeta.week,
      missionTitle: targetMission?.title || "Mission",
    });
  };

  const handleOpenFutureCra = () => {
    onOpenWeeklyCra({
      missionId: targetMissionId,
      isoYear: isoMeta.year,
      isoWeek: isoMeta.week,
      missionTitle: targetMission?.title || "Mission",
    });
  };

  return (
    <div className="grid grid-cols-[44px_repeat(7,1fr)] border-b border-zinc-100 last:border-b-0">
      {/* Leading CRA-Send sticky cell */}
      <div className="border-r border-zinc-100 bg-zinc-50/50 flex flex-col items-center justify-center p-1 gap-1 min-h-[104px]">
        {showWeekBtn ? (
          sentForTarget ? (
            <>
              <button
                type="button"
                onClick={handleOpenWeeklyCra}
                data-testid={`wkcra-sent-${isoMeta.year}-${isoMeta.week}`}
                title={`Envoyé · ${new Date(sentForTarget.sent_at).toLocaleDateString()} — cliquer pour relire`}
                className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white inline-flex items-center justify-center transition-all hover:scale-110 ring-1 ring-emerald-200"
              >
                <Eye size={14} />
              </button>
              <span className="text-[9px] font-mono text-emerald-700">S{isoMeta.week}</span>
              {weekTotalHours > 0 && (
                <span
                  className="text-[9px] font-mono text-emerald-700/70"
                  data-testid={`week-total-${isoMeta.year}-${isoMeta.week}`}
                >
                  {weekTotalHours}h · {Number(weekTotalDays.toFixed(2))}j
                </span>
              )}
            </>
          ) : isFutureRow ? (
            <>
              <button
                type="button"
                onClick={handleOpenFutureCra}
                data-testid={`wkcra-future-${isoMeta.year}-${isoMeta.week}`}
                title="Semaine future · cliquez pour pré-annoncer un congé (Mon..Ven 8h)"
                className="w-8 h-8 rounded-md bg-amber-50 border border-amber-200 text-amber-600 hover:bg-amber-100 inline-flex items-center justify-center transition-colors"
              >
                <Lock size={11} />
              </button>
              <span className="text-[9px] font-mono text-amber-600">S{isoMeta.week}</span>
              {weekTotalHours > 0 && (
                <span
                  className="text-[9px] font-mono text-amber-600/70"
                  data-testid={`week-total-${isoMeta.year}-${isoMeta.week}`}
                >
                  {weekTotalHours}h · {Number(weekTotalDays.toFixed(2))}j
                </span>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleOpenWeeklyCra}
                data-testid={`wkcra-open-${isoMeta.year}-${isoMeta.week}`}
                title={`Send weekly CRA · Semaine ${isoMeta.week}${targetMission ? ` · ${targetMission.title}` : ""}`}
                className="w-8 h-8 rounded-md border border-zinc-200 bg-white hover:bg-zinc-950 hover:text-white text-zinc-600 inline-flex items-center justify-center transition-colors"
              >
                <Send size={12} />
              </button>
              <span className="text-[9px] font-mono text-zinc-400">S{isoMeta.week}</span>
              {weekTotalHours > 0 && (
                <span
                  className="text-[9px] font-mono text-zinc-500"
                  data-testid={`week-total-${isoMeta.year}-${isoMeta.week}`}
                >
                  {weekTotalHours}h · {Number(weekTotalDays.toFixed(2))}j
                </span>
              )}
            </>
          )
        ) : null}
      </div>

      {/* Day cells */}
      {row.map((d, idx) => {
        const entries = d ? entriesByDay[d] || [] : [];
        const totalHours = entries.reduce((a, e) => a + e.hours, 0);
        const isToday = d && year === today.getFullYear() && month === today.getMonth() + 1 && d === today.getDate();
        const dateStr = d ? `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
        const dow = d ? new Date(year, month - 1, d).getDay() : null;
        const isWeekend = d && (dow === 0 || dow === 6);
        const holidayInfo = dateStr ? holidays[dateStr] : null;
        // Iter-193 — `holidays` value is now `{name, country}` (was a plain string).
        // Keep backward compatibility in case any caller still passes a string.
        const holidayName = typeof holidayInfo === "string"
          ? holidayInfo
          : (holidayInfo?.name || null);
        const holidayCountry = typeof holidayInfo === "object" ? (holidayInfo?.country || null) : null;
        const isNonWorking = isWeekend || !!holidayName;
        return (
          <DayCell
            key={`${idx}`}
            d={d}
            isToday={isToday}
            isWeekend={isWeekend}
            isNonWorking={isNonWorking}
            holidayName={holidayName}
            holidayCountry={holidayCountry}
            locked={locked}
            entries={entries}
            totalHours={totalHours}
            missions={missions}
            user={user}
            leaveBalance={leaveBalance}
            congeOverflowMap={congeOverflowMap}
            onTap={() => {
              if (!d) return;
              // Iter-115/116/121: on MOBILE, tap always opens the edit sheet — the user
              // picks activity / mission / hours / delete inside the sheet. This is more
              // predictable than direct +2h (which silently no-ops when no mission is
              // selected in the top selector bar). On DESKTOP, keep the legacy +2h click.
              if (isMobile && onOpenDaySheet) onOpenDaySheet(d);
              else if (!locked) onDayClick(d, 2);
            }}
            onDoubleTap={() => {
              // Desktop double-click adds +8h. On mobile, the sheet is already open
              // from the first tap so this callback is effectively unreachable.
              if (!d || locked) return;
              if (!isMobile) onDayClick(d, 8);
            }}
            onLongPress={() => {
              if (!d || locked) return;
              // Desktop right-click → delete entries matching the current selector context.
              // Mobile long-press also deletes for power users who want a shortcut; an
              // empty day falls back to opening the sheet.
              const selCtxMatches = entries.filter((e) =>
                e.activity_type === selectedActivity &&
                (selectedActivity !== "MISSION" || e.mission_id === selectedMission)
              );
              const toDelete = selCtxMatches.length > 0
                ? selCtxMatches
                : (entries.length > 0 ? [entries[entries.length - 1]] : []);
              if (toDelete.length > 0) {
                toDelete.forEach((e) => onDeleteEntry(e));
                return;
              }
              if (isMobile && onOpenDaySheet) onOpenDaySheet(d);
            }}
            onDeleteEntry={onDeleteEntry}
            isMobile={isMobile}
          />
        );
      })}
    </div>
  );
}

/**
 * DayCellWrapper — Iter-64.
 *
 * Supports both desktop (click + right-click) and mobile (tap + long-press) gestures.
 * Tap = +2h, long-press / right-click = +8h (full day).
 */
function DayCell({
  d, isToday, isWeekend, isNonWorking, holidayName, holidayCountry, locked,
  entries, totalHours, missions, user, leaveBalance, congeOverflowMap,
  onTap, onDoubleTap, onLongPress, onDeleteEntry, isMobile,
}) {
  const handlers = useLongPress({ onTap, onDoubleTap, onLongPress });
  // Iter-193c — render a flag image (cross-browser) next to the holiday name.
  const showFlag = isValidCountryCode(holidayCountry);
  return (
    <div
      data-testid={d ? `cal-day-${d}` : undefined}
      {...handlers}
      className={`cra-day min-h-[104px] min-w-0 border-r border-b border-zinc-100 p-2 flex flex-col relative select-none overflow-hidden ${
        d ? (locked ? "cursor-not-allowed bg-zinc-50" : "cursor-pointer") : "bg-zinc-50/50"
      } ${isNonWorking ? "bg-zinc-100/70" : ""} ${isToday ? "ring-1 ring-inset ring-zinc-950" : ""}`}
      style={{ touchAction: "manipulation", WebkitTouchCallout: "none" }}
      title={holidayName ? `Holiday${holidayCountry ? ` (${holidayCountry})` : ""}: ${holidayName}` : (isWeekend ? "Weekend" : undefined)}
    >
      {d && (
        <>
          <div className="flex items-start justify-between gap-1 mb-2">
            <span className={`text-xs font-mono shrink-0 ${
              isToday ? "font-bold" : isNonWorking ? "text-zinc-400" : "text-zinc-500"
            }`}>{d}</span>
            {holidayName && (
              <span
                className="text-[9px] font-mono text-amber-700 ml-1 flex items-start gap-1 min-w-0 flex-1 justify-end"
                title={`${holidayCountry || ""} ${holidayName}`.trim()}
                data-testid={`holiday-label-${d}`}
              >
                {showFlag && (
                  <span
                    data-testid={`holiday-flag-${d}`}
                    className="inline-flex items-center shrink-0 mt-[1px]"
                  >
                    <Flag code={holidayCountry} size={10} alt={false} />
                  </span>
                )}
                {/* Iter-193e — wrap long holiday names on up to 2 lines instead of
                    truncating them out of the cell (which also hid the flag). */}
                <span className="line-clamp-2 break-words text-right leading-tight">
                  {holidayName}
                </span>
              </span>
            )}
            {totalHours > 0 && <span className="text-[10px] font-mono text-zinc-500 shrink-0">{totalHours}h</span>}
          </div>
          <div className="flex flex-col gap-1 mt-auto">
            {entries.map((e) => {
              const _m = e.activity_type === "MISSION"
                ? missions.find((mm) => mm.mission_id === e.mission_id)
                : null;
              const bg = e.activity_type === "MISSION"
                ? colorOf(_m || { mission_id: e.mission_id })
                : ACTIVITY_COLORS[e.activity_type];
              const label = e.activity_type === "MISSION"
                ? (_m?.title || "Mission")
                : (e.activity_type === "CONGE" && user.is_freelance
                  ? "Leave"
                  : ACTIVITY_LABELS[e.activity_type]);
              const isOverflowConge = e.activity_type === "CONGE" && (
                congeOverflowMap[e.entry_id] || (leaveBalance?.applies && leaveBalance.overflow > 0)
              );
              const finalBg = isOverflowConge ? "#DC2626" : bg;
              return (
                <div
                  key={e.entry_id}
                  data-testid={`chip-${e.entry_id}`}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    // Iter-117: mobile gestures — long-press on day cell handles delete.
                    // Chip click on mobile is absorbed (no-op); desktop keeps click=delete.
                    if (isMobile) return;
                    onDeleteEntry(e);
                  }}
                  className="group text-[10px] px-1.5 py-0.5 text-white rounded-sm font-medium flex items-center justify-between hover:opacity-80 min-w-0 max-w-full"
                  style={{
                    background: finalBg,
                    cursor: isMobile ? "default" : "pointer",
                    ...(isOverflowConge ? { boxShadow: "inset 0 0 0 1px #fff5" } : {}),
                  }}
                  title={isOverflowConge
                    ? `${label} · ${e.hours}h — DÉPASSEMENT, comptera comme crédit congé sur l'année prochaine.`
                    : (isMobile ? `${label} · ${e.hours}h` : `${label} · ${e.hours}h — click to delete`)}
                >
                  <span className="truncate min-w-0 flex-1">
                    {e.hours}h · {label}{isOverflowConge ? " ⚠" : ""}
                  </span>
                  {!isMobile && <Trash2 size={9} className="opacity-0 group-hover:opacity-100 shrink-0 ml-1" />}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
